<template>
  <div>
    <Header />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Payroll</h5>
        <p class="page-sub">Overview of payroll processing and history</p>
      </div>
      <div class="page-actions">
        <button class="btn-export-sm" @click="openExportModal('csv')" :disabled="exportLoading"><font-awesome-icon icon="file-csv" /> CSV</button>
        <button class="btn-export-sm btn-export-sm--pdf" @click="openExportModal('pdf')" :disabled="exportLoading"><font-awesome-icon icon="file-pdf" /> PDF</button>
        <button class="btn-primary-action" @click="openCreateModal">
          <font-awesome-icon icon="plus" class="me-1" /> Create New Payrun
        </button>
      </div>
    </div>

    <!-- STAT CARDS -->
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="money-bill-wave" /></div>
        <div>
          <p class="stat-label">Previous Month Salary</p>
          <p class="stat-val">
            <span v-if="loadingDashboard" class="skeleton-val"></span>
            <span v-else>{{ formatCurrency(stats.totalSalaryPreviousMonth) }}</span>
          </p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--green"><font-awesome-icon icon="users" /></div>
        <div>
          <p class="stat-label">Pay Grades</p>
          <p class="stat-val">
            <span v-if="loadingDashboard" class="skeleton-val"></span>
            <span v-else>{{ stats.paygradeCount }}</span>
          </p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--amber"><font-awesome-icon icon="money-bill-wave" /></div>
        <div>
          <p class="stat-label">Expected This Month</p>
          <p class="stat-val">
            <span v-if="loadingDashboard" class="skeleton-val"></span>
            <span v-else>{{ formatCurrency(stats.totalSalaryExpectedCurrentMonth) }}</span>
          </p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="calendar" /></div>
        <div>
          <p class="stat-label">Payroll Runs</p>
          <p class="stat-val">
            <span v-if="loadingDashboard" class="skeleton-val"></span>
            <span v-else>{{ stats.payrollRuns || total }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- CHART CARD -->
    <div class="chart-card">
      <div class="chart-card-hd">
        <h3 class="chart-title">Payroll Trends</h3>
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-dot legend-dot--solid"></span> Total Paid</span>
          <span class="legend-item"><span class="legend-dot legend-dot--dashed"></span> Employees</span>
        </div>
      </div>
      <div class="chart-body">
        <div class="chart-y-axis">
          <span>{{ chartMaxLabel }}</span>
          <span>{{ chartMidLabel }}</span>
          <span>\u20A60</span>
        </div>
        <div class="chart-area">
          <svg class="chart-svg" viewBox="0 0 800 280" preserveAspectRatio="none">
            <line v-for="y in [0,70,140,210,280]" :key="y" x1="0" :y1="y" x2="800" :y2="y" stroke="#e4eaf2" stroke-width="1" />
            <polyline v-if="paidPoints" :points="paidPoints" fill="none" stroke="#004AAD" stroke-width="3" stroke-linejoin="round" />
            <polyline v-if="empPoints"  :points="empPoints"  fill="none" stroke="#16a34a" stroke-width="2.5" stroke-dasharray="6,4" stroke-linejoin="round" />
            <circle v-for="(pt, i) in paidPointsArr" :key="'p'+i" :cx="pt.x" :cy="pt.y" r="4" fill="#004AAD" />
          </svg>
          <div class="chart-x-axis">
            <span v-for="(m, i) in chartData.months" :key="i" class="chart-x-label">{{ m }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- PAYROLL RUNS TABLE -->
    <div class="data-card">
      <div class="data-card-hd">
        <div>
          <h3 class="data-card-title">Payroll Runs</h3>
          <p class="data-card-sub">View and manage all payroll processing history</p>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            type="text"
            class="search-input"
            placeholder="Search payroll runs..."
            v-model="tableData.search"
            @input="debouncedFetch"
          />
        </div>

        <select class="filter-select" v-model="tableData.status" @change="resetAndFetch">
          <option :value="['open','processed','approved','posted']">All Statuses</option>
          <option :value="['open']">Open</option>
          <option :value="['processed']">Processed</option>
          <option :value="['approved']">Approved</option>
          <option :value="['paid']">Paid</option>
          <option :value="['posted']">Posted</option>
        </select>

        <div class="date-range-wrap">
          <input type="date" class="filter-date" v-model="tableData.from_date" @change="resetAndFetch" placeholder="From" />
          <span class="date-sep">—</span>
          <input type="date" class="filter-date" v-model="tableData.to_date"   @change="resetAndFetch" placeholder="To" />
        </div>

        <button v-if="hasActiveFilters" class="btn-clear" @click="clearFilters">
          <font-awesome-icon icon="times" class="me-1" /> Clear
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loadingRuns" class="state-block">
        <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
        <p class="mt-2 text-muted-cell">Loading payroll runs...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="payrollRuns.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="calendar" /></div>
        <p class="state-title">No payroll runs found</p>
        <p class="state-sub">{{ hasActiveFilters ? 'No results match your filters.' : 'Create a payrun to get started.' }}</p>
        <button v-if="!hasActiveFilters" class="btn-primary-action mt-4" @click="openCreateModal">
          <font-awesome-icon icon="plus" class="me-1" /> Create New Payrun
        </button>
        <button v-else class="btn-clear mt-4" @click="clearFilters">Clear Filters</button>
      </div>

      <!-- Table -->
      <template v-else>
        <div class="table-toolbar">
          <span class="table-count">
            <strong>{{ total }}</strong> payroll run{{ total !== 1 ? 's' : '' }}
          </span>
          <select class="per-page-select" v-model="tableData.length" @change="resetAndFetch">
            <option :value="5">5 / page</option>
            <option :value="10">10 / page</option>
            <option :value="20">20 / page</option>
            <option :value="50">50 / page</option>
            <option :value="100">100 / page</option>
            <option :value="200">200 / page</option>
            <option :value="500">500 / page</option>
            <option :value="1000">1000 / page</option>
          </select>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Payroll Period</th>
                <th>Employees</th>
                <th>Total Paid</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="run in payrollRuns" :key="run.id">
                <td>
                  <span class="run-period">{{ run.startDate }} — {{ run.endDate }}</span>
                  <span class="run-month">{{ run.period }}</span>
                </td>
                <td class="text-muted">{{ run.employees }}</td>
                <td class="text-muted">{{ run.totalPaid != null ? formatCurrency(run.totalPaid) : '—' }}</td>
                <td>
                  <span class="status-badge" :class="statusClass(run.status)">{{ run.status }}</span>
                </td>
                <td class="col-actions">
                  <div class="row-menu" @click.stop>
                    <button class="row-menu-btn" @click="toggleMenu($event, run)">
                      <span class="hamburger-bar"></span>
                      <span class="hamburger-bar"></span>
                      <span class="hamburger-bar"></span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-bar">
          <span class="page-info">
            Showing {{ from }}–{{ to }} of {{ total }}
          </span>
          <div class="page-btns">
            <button class="page-btn" :disabled="currentPage === 1" @click="goPage(1)">
              <font-awesome-icon icon="angle-double-left" />
            </button>
            <button class="page-btn" :disabled="currentPage === 1" @click="goPage(currentPage - 1)">
              <font-awesome-icon icon="angle-left" />
            </button>
            <button
              v-for="p in visiblePages" :key="p"
              class="page-btn"
              :class="{ 'page-btn--active': p === currentPage }"
              @click="goPage(p)"
            >{{ p }}</button>
            <button class="page-btn" :disabled="currentPage === lastPage" @click="goPage(currentPage + 1)">
              <font-awesome-icon icon="angle-right" />
            </button>
            <button class="page-btn" :disabled="currentPage === lastPage" @click="goPage(lastPage)">
              <font-awesome-icon icon="angle-double-right" />
            </button>
          </div>
        </div>
      </template>
    </div>

  <!-- ══════════════════════════════════════
       CREATE PAYRUN MODAL
  ══════════════════════════════════════ -->
  <div class="cr-overlay" :class="{ open: createModalOpen }" @click.self="!createSubmitting && closeCreateModal()">
    <div class="cr-box">

      <!-- Header -->
      <div class="cr-hd">
        <div class="cr-hd-inner">
          <div class="cr-hd-icon">
            <font-awesome-icon :icon="createStep === 0 ? 'plus' : (createType === 'uploaded_file' ? 'file-excel' : 'money-bill-wave')" />
          </div>
          <div>
            <h3 class="cr-title">{{ createStep === 0 ? 'Create New Payrun' : (createType === 'uploaded_file' ? 'Upload Payroll File' : 'New Payrun') }}</h3>
            <p class="cr-sub">{{ createStep === 0 ? 'Choose how to create this payrun' : 'Fill in the payroll period details' }}</p>
          </div>
        </div>
        <button class="cr-close" :disabled="createSubmitting" @click="closeCreateModal">
          <font-awesome-icon icon="times" />
        </button>
      </div>

      <!-- ── Step 0: Type picker ─────────────────── -->
      <div v-if="createStep === 0" class="cr-bd">
        <div class="cr-type-grid">
          <button class="cr-type-card" @click="selectType('standard')">
            <div class="cr-type-icon cr-type-icon--blue">
              <font-awesome-icon icon="users" />
            </div>
            <h4 class="cr-type-title">Build from Employees</h4>
            <p class="cr-type-desc">Automatically calculate payroll for all active employees based on their pay grades and salary components.</p>
            <span class="cr-type-arrow"><font-awesome-icon icon="arrow-right" /></span>
          </button>

          <button class="cr-type-card" @click="selectType('uploaded_file')">
            <div class="cr-type-icon cr-type-icon--green">
              <font-awesome-icon icon="file-excel" />
            </div>
            <h4 class="cr-type-title">Upload Excel File</h4>
            <p class="cr-type-desc">Import employee salary data from a spreadsheet. Download the template, fill it in, and upload.</p>
            <span class="cr-type-arrow"><font-awesome-icon icon="arrow-right" /></span>
          </button>
        </div>
      </div>

      <!-- ── Step 1: Form ────────────────────────── -->
      <div v-else class="cr-bd">

        <!-- Upload banner + template download -->
        <div v-if="createType === 'uploaded_file'" class="cr-upload-banner">
          <div class="cr-upload-banner-left">
            <font-awesome-icon icon="info-circle" class="cr-banner-icon" />
            <p>Download the template, fill in employee salary details, then upload below.</p>
          </div>
          <button class="cr-template-btn" @click="downloadTemplate">
            <font-awesome-icon icon="download" class="me-1" /> Download Template
          </button>
        </div>

        <div class="cr-form">
          <!-- Period name -->
          <div class="cr-field">
            <label class="cr-label">Period Name <span class="cr-req">*</span></label>
            <input class="cr-input" v-model="createForm.name" type="text" placeholder="e.g. July 2026 Payroll" />
          </div>

          <!-- Payroll month -->
          <div class="cr-field">
            <label class="cr-label">Payroll Month <span class="cr-req">*</span></label>
            <input class="cr-input" v-model="createForm.payroll_month" type="month" />
          </div>

          <!-- Date row -->
          <div class="cr-field-row">
            <div class="cr-field">
              <label class="cr-label">Start Date <span class="cr-req">*</span></label>
              <input class="cr-input" v-model="createForm.start_date" type="date" />
            </div>
            <div class="cr-field">
              <label class="cr-label">End Date <span class="cr-req">*</span></label>
              <input class="cr-input" v-model="createForm.end_date" type="date" />
            </div>
          </div>

          <!-- Payment date -->
          <div class="cr-field">
            <label class="cr-label">Payment Date <span class="cr-req">*</span></label>
            <input class="cr-input" v-model="createForm.payment_date" type="date" />
          </div>

          <!-- Frequency -->
          <div class="cr-field">
            <label class="cr-label">Pay Frequency</label>
            <select class="cr-input" v-model="createForm.period_frequency">
              <option value="">Select frequency</option>
              <option value="monthly">Monthly</option>
              <option value="bi-weekly">Bi-Weekly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <!-- File upload (upload flow only) -->
          <div v-if="createType === 'uploaded_file'" class="cr-field">
            <label class="cr-label">Upload Excel File <span class="cr-req">*</span></label>
            <div class="cr-file-wrap" :class="{ 'cr-file-wrap--has': createForm.import_file }">
              <input type="file" class="cr-file-input" accept=".xlsx,.csv" @change="onFileChange" ref="fileInput" />
              <div class="cr-file-inner">
                <font-awesome-icon :icon="createForm.import_file ? 'check-circle' : 'cloud-upload-alt'" class="cr-file-icon" :class="{ 'cr-file-icon--ok': createForm.import_file }" />
                <span class="cr-file-text">{{ createForm.import_file ? createForm.import_file.name : 'Click to select .xlsx or .csv' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="cr-ft" v-if="createStep === 1">
        <button class="cr-btn-ghost" :disabled="createSubmitting" @click="createStep = 0">
          <font-awesome-icon icon="arrow-left" class="me-1" /> Back
        </button>
        <button class="cr-btn-primary" :disabled="createSubmitting" @click="submitCreate">
          <span v-if="createSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <font-awesome-icon v-else icon="check" class="me-1" />
          {{ createSubmitting ? 'Creating...' : 'Create Payrun' }}
        </button>
      </div>

    </div>
  </div>
  <div v-if="createModalOpen" class="cr-backdrop" />

  <!-- ── Row action dropdown (rendered outside table to avoid overflow clipping) ── -->
  <div
    v-if="activeMenuRun"
    class="row-menu-dropdown"
    :style="{ top: menuPos.top + 'px', left: menuPos.left + 'px' }"
    @click.stop
  >
    <button v-if="Number(activeMenuRun.employees || 0) === 0"
      class="row-menu-item"
      @click="continuePayrun(activeMenuRun.reference); activeMenuRun = null">
      <font-awesome-icon icon="arrow-right" class="row-menu-icon row-menu-icon--green" /> Continue
    </button>
    <button v-else
      class="row-menu-item"
      @click="$router.push('/payroll/' + activeMenuRun.id); activeMenuRun = null">
      <font-awesome-icon icon="eye" class="row-menu-icon row-menu-icon--blue" /> View Details
    </button>
    <template v-if="canDeleteRun(activeMenuRun.status)">
      <div class="row-menu-divider"></div>
      <button
        class="row-menu-item row-menu-item--danger"
        :disabled="deletingId === activeMenuRun.id"
        @click="confirmDelete(activeMenuRun); activeMenuRun = null">
        <font-awesome-icon :icon="deletingId === activeMenuRun.id ? 'spinner' : 'trash'" :spin="deletingId === activeMenuRun.id" class="row-menu-icon" /> Delete
      </button>
    </template>
  </div>

  <!-- ── Delete Confirmation Modal ── -->
  <transition name="fade">
    <div v-if="deleteTarget" class="del-backdrop" @click.self="deleteTarget = null">
      <div class="del-box">
        <div class="del-icon-wrap">
          <font-awesome-icon icon="exclamation-triangle" class="del-icon" />
        </div>
        <h3 class="del-title">Delete Payroll Run?</h3>
        <p class="del-desc">
          This will permanently delete the payroll run for
          <strong>{{ deleteTarget?.period }}</strong> along with all payroll items and any pending approval requests. This cannot be undone.
        </p>
        <div class="del-actions">
          <button class="del-btn-cancel" :disabled="deletingId !== null" @click="deleteTarget = null">Cancel</button>
          <button class="del-btn-confirm" :disabled="deletingId !== null" @click="executeDelete">
            <font-awesome-icon :icon="deletingId !== null ? 'spinner' : 'trash'" :spin="deletingId !== null" class="me-1" />
            {{ deletingId !== null ? 'Deleting…' : 'Yes, Delete' }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- ── Export Column Picker ── -->
  <transition name="fade">
    <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
      <div class="col-picker-modal">
        <div class="col-picker-header">
          <div>
            <h3 class="col-picker-title">Select Columns</h3>
            <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
          </div>
          <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="col-picker-actions">
          <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
          <span class="col-picker-sep">·</span>
          <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
          <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
        </div>
        <div class="col-picker-grid">
          <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
            <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
            <span class="col-picker-label">{{ col.label }}</span>
          </label>
        </div>
        <div class="col-picker-footer">
          <button class="col-picker-btn-ghost" @click="showExportModal = false">Cancel</button>
          <button class="col-picker-btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
            Export {{ pendingExportFmt.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const EMPTY_FORM = () => ({
  name:             '',
  payroll_month:    '',
  start_date:       '',
  end_date:         '',
  payment_date:     '',
  period_frequency: 'monthly',
  import_file:      null,
});

const DEFAULT_CHART = {
  months:         ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  totalPaid:      new Array(12).fill(0),
  totalEmployees: new Array(12).fill(0),
};

export default {
  name: 'PayrollDashboard',
  components: { Header },

  data() {
    return {
      loadingDashboard: true,
      loadingRuns:      true,

      stats: {
        paygradeCount:                   0,
        totalSalaryPreviousMonth:        0,
        totalSalaryExpectedCurrentMonth: 0,
        payrollRuns:                     0,
      },

      chartData:   { ...DEFAULT_CHART },
      payrollRuns: [],

      // Pagination
      total:       0,
      currentPage: 1,
      lastPage:    1,
      from:        0,
      to:          0,

      tableData: {
        draw:      0,
        length:    5,
        page:      1,
        search:    '',
        status:    ['open','processed','approved','paid','posted'],
        from_date: '',
        to_date:   '',
        column:    0,
        dir:       'desc',
      },

      // Create payrun modal
      createModalOpen:    false,
      createStep:         0,
      createType:         '',
      createForm:         EMPTY_FORM(),
      createSubmitting:   false,

      activeMenuRun:      null,
      menuPos:            { top: 0, left: 0 },
      deleteTarget:       null,
      deletingId:         null,

      exportLoading:      false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'period',     label: 'Period',     pdfWidth: 150 },
        { key: 'start_date', label: 'Start Date', pdfWidth: 115 },
        { key: 'end_date',   label: 'End Date',   pdfWidth: 115 },
        { key: 'employees',  label: 'Employees',  pdfWidth: 95 },
        { key: 'total_paid', label: 'Total Paid', pdfWidth: 115 },
        { key: 'status',     label: 'Status',     pdfWidth: 95 },
      ],

      toast: useToast(),
    };
  },

  computed: {
    hasActiveFilters() {
      const def = ['open','processed','approved','posted'];
      const statusChanged = JSON.stringify(this.tableData.status) !== JSON.stringify(def);
      return this.tableData.search || statusChanged || this.tableData.from_date || this.tableData.to_date;
    },

    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end   = Math.min(this.lastPage, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },

    chartMaxPaid() { return Math.max(...this.chartData.totalPaid, 1); },
    chartMaxLabel() { return '\u20A6' + (this.chartMaxPaid / 1000).toFixed(0) + 'K'; },
    chartMidLabel() { return '\u20A6' + (this.chartMaxPaid / 2000).toFixed(0) + 'K'; },

    paidPointsArr() {
      const data = this.chartData.totalPaid;
      if (!data.length) return [];
      return data.map((val, i) => ({
        x: (i / (data.length - 1 || 1)) * 800,
        y: 280 - (val / this.chartMaxPaid) * 280,
      }));
    },
    paidPoints() { return this.paidPointsArr.map(p => `${p.x},${p.y}`).join(' '); },
    empPoints() {
      const data   = this.chartData.totalEmployees;
      const maxEmp = Math.max(...data, 1);
      if (!data.length) return '';
      return data.map((val, i) => {
        const x = (i / (data.length - 1 || 1)) * 800;
        const y = 280 - (val / maxEmp) * 280 * 0.5;
        return `${x},${y}`;
      }).join(' ');
    },
  },

  created() {
    this.fetchDashboard();
    this.fetchPayrollRuns();
  },

  mounted() {
    this._closeMenu = () => { this.activeMenuRun = null; };
    document.addEventListener('click', this._closeMenu);
    window.addEventListener('scroll', this._closeMenu, true);
    window.addEventListener('resize', this._closeMenu);
  },

  beforeUnmount() {
    document.removeEventListener('click', this._closeMenu);
    window.removeEventListener('scroll', this._closeMenu, true);
    window.removeEventListener('resize', this._closeMenu);
  },

  methods: {
    // -- Dashboard -----------------------------------------------------
    fetchDashboard() {
      this.loadingDashboard = true;
      const year = new Date().getFullYear();
      Promise.allSettled([
        axios.get('/payroll/dashboard'),
        axios.get('/payroll/dashboard/chart-data', { params: { year, months: 12 } }),
      ]).then(([statsRes, chartRes]) => {
        if (statsRes.status === 'fulfilled') {
          const p = statsRes.value.data?.data || {};
          this.stats = {
            paygradeCount:                   Number(p.paygrade_count ?? 0),
            totalSalaryPreviousMonth:        Number(p.total_salary_previous_month ?? 0),
            totalSalaryExpectedCurrentMonth: Number(p.total_salary_expected_current_month ?? 0),
            payrollRuns:                     Number(p.payroll_runs ?? p.payroll_runs_count ?? p.total_runs ?? 0),
          };
        }
        if (chartRes.status === 'fulfilled') {
          this.chartData = this.normaliseChart(chartRes.value.data?.data ?? chartRes.value.data);
        }
      }).finally(() => { this.loadingDashboard = false; });
    },

    // -- Payroll runs --------------------------------------------------
    fetchPayrollRuns() {
      this.loadingRuns = true;
      axios.get('/payroll/period', { params: { ...this.tableData, page: this.currentPage } })
        .then(({ data }) => {
          const meta = data.data;
          const rows = Array.isArray(meta?.data) ? meta.data
            : Array.isArray(meta) ? meta : [];

          this.payrollRuns = rows.map(run => {
            const range = this.formatDateRange(run.start_date, run.end_date);
            return {
              id:        run.id,
              reference: run.reference,
              period:    range.period,
              startDate: range.startStr,
              endDate:   range.endStr,
              employees: run.employee_count || 0,
              totalPaid: run.total_net ?? null,
              status:    run.status || 'draft',
            };
          });

          // Pagination meta
          this.total       = meta?.total       ?? rows.length;
          this.lastPage    = meta?.last_page   ?? 1;
          this.from        = meta?.from        ?? (rows.length ? 1 : 0);
          this.to          = meta?.to          ?? rows.length;
          this.currentPage = meta?.current_page ?? this.currentPage;
        })
        .catch(() => {
          this.toast.error('Could not load payroll runs.');
          this.payrollRuns = [];
        })
        .finally(() => { this.loadingRuns = false; });
    },

    resetAndFetch() {
      this.currentPage = 1;
      this.fetchPayrollRuns();
    },

    goPage(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      this.fetchPayrollRuns();
    },

    debouncedFetch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.resetAndFetch(), 300);
    },

    clearFilters() {
      this.tableData.search    = '';
      this.tableData.status    = ['open','processed','approved','posted'];
      this.tableData.from_date = '';
      this.tableData.to_date   = '';
      this.resetAndFetch();
    },

    continuePayrun(ref) {
      this.$router.push(`/payroll/payrun?step=2&payrunRef=${ref}`);
    },

    toggleMenu(event, run) {
      if (this.activeMenuRun?.id === run.id) {
        this.activeMenuRun = null;
        return;
      }
      const btn  = event.currentTarget;
      const rect = btn.getBoundingClientRect();
      const dropdownWidth = 160;
      const left = rect.right - dropdownWidth < 0 ? rect.left : rect.right - dropdownWidth;
      this.menuPos      = { top: rect.bottom + 6, left };
      this.activeMenuRun = run;
    },

    canDeleteRun(status) {
      return ['open', 'processed'].includes((status || '').toLowerCase());
    },

    confirmDelete(run) {
      this.deleteTarget = run;
    },

    async executeDelete() {
      if (!this.deleteTarget) return;
      this.deletingId = this.deleteTarget.id;
      try {
        await axios.delete(`/payroll/period/${this.deleteTarget.id}`);
        this.toast.success('Payroll run deleted successfully.');
        this.deleteTarget = null;
        this.fetchPayrollRuns();
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to delete payroll run.');
      } finally {
        this.deletingId = null;
      }
    },

    // -- Create payrun modal -------------------------------------------
    openCreateModal() {
      this.createStep       = 0;
      this.createType       = '';
      this.createForm       = EMPTY_FORM();
      this.createModalOpen  = true;
    },

    closeCreateModal() {
      this.createModalOpen  = false;
    },

    selectType(type) {
      if (type === 'standard') {
        this.closeCreateModal();
        this.$router.push('/create-payroll');
        return;
      }
      this.createType = type;
      this.createStep = 1;
    },

    onFileChange(e) {
      this.createForm.import_file = e.target.files[0] || null;
    },

    downloadTemplate() {
      const headers = [
        'employee_no',
        'first_name',
        'last_name',
        'email',
        'phone_number',
        'basic_salary',
        'hourly_rate',
        'total_hours',
        'bank_name',
        'bank_account_no',
        'bank_sortcode',
      ];

      const example = [
        'EMP001',
        'John',
        'Doe',
        'john.doe@example.com',
        '08012345678',
        '250000',
        '0',
        '0',
        'First Bank',
        '1234567890',
        '',
      ];

      const csv = [headers.join(','), example.join(',')].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'payroll_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    submitCreate() {
      const f = this.createForm;
      if (!f.name || !f.start_date || !f.end_date || !f.payment_date || !f.payroll_month) {
        this.toast.error('Please fill in all required fields.');
        return;
      }
      if (this.createType === 'uploaded_file' && !f.import_file) {
        this.toast.error('Please select an Excel or CSV file to upload.');
        return;
      }

      this.createSubmitting = true;

      const body = new FormData();
      body.append('name',             f.name);
      body.append('payroll_month',    f.payroll_month);
      body.append('start_date',       f.start_date);
      body.append('end_date',         f.end_date);
      body.append('payment_date',     f.payment_date);
      body.append('period_frequency', f.period_frequency || 'monthly');
      body.append('process_type',     this.createType);
      if (f.import_file) body.append('import_file', f.import_file);

      axios.post('/payroll/period', body, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(({ data }) => {
          const id = data?.data?.id;
          this.toast.success('Payrun created successfully.');
          this.closeCreateModal();
          if (id) {
            this.$router.push('/payroll/' + id);
          } else {
            this.fetchPayrollRuns();
          }
        })
        .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to create payrun.'))
        .finally(() => { this.createSubmitting = false; });
    },

    // -- Export -------------------------------------------------------
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },

    getRunValue(run, key) {
      switch (key) {
        case 'period':     return run.period || '—';
        case 'start_date': return run.startDate || '—';
        case 'end_date':   return run.endDate || '—';
        case 'employees':  return run.employees ?? '—';
        case 'total_paid': return run.totalPaid != null ? this.formatCurrency(run.totalPaid) : '—';
        case 'status':     return run.status || '—';
        default:           return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.tableData, page: 1, length: 10000 };
        const { data } = await axios.get('/payroll/period', { params });
        const meta = data.data;
        const rawRows = Array.isArray(meta?.data) ? meta.data : Array.isArray(meta) ? meta : [];
        const rows = rawRows.map(run => {
          const range = this.formatDateRange(run.start_date, run.end_date);
          return { id: run.id, period: range.period, startDate: range.startStr, endDate: range.endStr, employees: run.employee_count || 0, totalPaid: run.total_net ?? null, status: run.status || 'draft' };
        });
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRunValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);

        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `payroll-runs-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable   = 761;
          const scale    = usable / cols.reduce((s, c) => s + c.pdfWidth, 0);
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69);
          doc.text('Payroll Runs', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize:7.5, cellPadding:3.5, textColor:[26,43,69], overflow:'ellipsize' },
            headStyles: { fillColor:[0,74,173], textColor:255, fontStyle:'bold', fontSize:7.5 },
            alternateRowStyles: { fillColor:[248,250,252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left:40, right:40 },
            didDrawPage(d) {
              doc.setFontSize(8); doc.setTextColor(192,202,214);
              doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height-16, { align:'center' });
            },
          });
          doc.save(`payroll-runs-${date}.pdf`);
        }
      } catch {
        this.toast.error('Export failed. Please try again.');
      } finally {
        this.exportLoading = false;
      }
    },

    // -- Helpers -------------------------------------------------------
    normaliseChart(payload) {
      if (!payload) return { ...DEFAULT_CHART };
      if (Array.isArray(payload)) {
        return {
          months:         payload.map(i => i?.month || ''),
          totalPaid:      payload.map(i => Number(i?.total || 0)),
          totalEmployees: new Array(payload.length).fill(0),
        };
      }
      const months = payload?.months || payload?.labels || DEFAULT_CHART.months;
      const totalPaid = payload?.totalPaid || payload?.total_paid || payload?.amounts || DEFAULT_CHART.totalPaid;
      const totalEmployees = payload?.totalEmployees || payload?.total_employees || payload?.employees || DEFAULT_CHART.totalEmployees;
      return {
        months:         Array.isArray(months)         ? months         : DEFAULT_CHART.months,
        totalPaid:      Array.isArray(totalPaid)      ? totalPaid.map(v => Number(v || 0))      : DEFAULT_CHART.totalPaid,
        totalEmployees: Array.isArray(totalEmployees) ? totalEmployees.map(v => Number(v || 0)) : DEFAULT_CHART.totalEmployees,
      };
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return { period: 'N/A', startStr: '—', endStr: '—' };
      const start = new Date(startDate);
      const end   = new Date(endDate);
      const opts  = { month: 'short', day: 'numeric', year: 'numeric' };
      return {
        period:   start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        startStr: start.toLocaleDateString('en-US', opts),
        endStr:   end.toLocaleDateString('en-US', opts),
      };
    },

    formatCurrency(val) {
      const num = Number(val || 0);
      if (num >= 1000000) return '\u20A6' + (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000)    return '\u20A6' + (num / 1000).toFixed(1) + 'K';
      return '\u20A6' + num.toLocaleString();
    },

    statusClass(status) {
      const map = {
        draft:     'status-badge--amber',
        open:      'status-badge--blue',
        processed: 'status-badge--purple',
        approved:  'status-badge--green',
        paid:      'status-badge--teal',
        posted:    'status-badge--indigo',
        cancelled: 'status-badge--red',
      };
      return map[(status || '').toLowerCase()] ?? 'status-badge--gray';
    },
  },
};
</script>


<style scoped>
/* -- Page ---------------------------------------------------------------- */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; gap:8px; }
.me-1 { margin-right:4px; }
.mt-2 { margin-top:8px; }
.mt-4 { margin-top:16px; }

/* -- Buttons ------------------------------------------------------------- */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
.btn-primary-action:hover { background:#003b8e; }
.btn-clear { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#64748b; font-size:12.5px; font-weight:600; padding:7px 12px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; transition:background .15s; font-family:inherit; white-space:nowrap; }
.btn-clear:hover { background:#e4eaf2; }

/* -- Stat cards ---------------------------------------------------------- */
.stat-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
.stat-card   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:14px; }
.stat-icon   { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.stat-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
.stat-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
.stat-icon--amber  { background:rgba(245,158,11,.1); color:#d97706; }
.stat-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
.stat-label  { font-size:12.5px; color:#7a90b0; margin:0 0 5px; }
.stat-val    { font-size:22px; font-weight:800; color:#1a2b45; margin:0; }
.skeleton-val { display:inline-block; width:60px; height:22px; background:#f0f4f8; border-radius:4px; animation:pulse 1.2s infinite; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

/* -- Chart card ---------------------------------------------------------- */
.chart-card    { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; margin-bottom:20px; }
.chart-card-hd { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.chart-title   { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.chart-legend  { display:flex; gap:16px; }
.legend-item   { display:flex; align-items:center; gap:7px; font-size:12px; color:#7a90b0; }
.legend-dot    { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
.legend-dot--solid  { background:#004AAD; }
.legend-dot--dashed { background:transparent; border:2px dashed #16a34a; }
.chart-body    { display:flex; gap:8px; align-items:stretch; }
.chart-y-axis  { display:flex; flex-direction:column; justify-content:space-between; padding:4px 0; font-size:11px; color:#94a3b8; width:52px; flex-shrink:0; text-align:right; }
.chart-area    { flex:1; min-width:0; }
.chart-svg     { width:100%; height:260px; display:block; }
.chart-x-axis  { display:flex; justify-content:space-between; padding-top:6px; }
.chart-x-label { font-size:11px; color:#94a3b8; }

/* -- Data card ----------------------------------------------------------- */
.data-card       { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-card-hd    { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; padding:20px 24px; border-bottom:1px solid #e4eaf2; }
.data-card-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.data-card-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* -- Filters ------------------------------------------------------------- */
.filters-bar  { display:flex; align-items:center; flex-wrap:wrap; gap:10px; padding:14px 20px; border-bottom:1px solid #f0f4f8; background:#fafbfd; }
.search-wrap  { position:relative; display:flex; align-items:center; flex:1; min-width:180px; max-width:280px; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { width:100%; padding:8px 12px 8px 34px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
.date-range-wrap { display:flex; align-items:center; gap:6px; }
.filter-date  { padding:8px 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
.filter-date:focus { border-color:#004AAD; }
.date-sep     { font-size:13px; color:#94a3b8; }

/* -- Table toolbar ------------------------------------------------------- */
.table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; border-bottom:1px solid #f0f4f8; }
.table-count   { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.per-page-select { padding:6px 10px; border:1px solid #dde5f0; border-radius:7px; font-size:12.5px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* -- States -------------------------------------------------------------- */
.state-block      { display:flex; flex-direction:column; align-items:center; padding:56px 24px; text-align:center; }
.state-icon       { width:56px; height:56px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:22px; margin-bottom:14px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-title      { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; font-size:13px; }

/* -- Table --------------------------------------------------------------- */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 20px; border-bottom:1px solid #e4eaf2; white-space:nowrap; text-align:left; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:14px 20px; font-size:13px; vertical-align:middle; }
.col-actions { width:140px; text-align:left; }
.text-muted  { color:#4a5e78; }
.run-period  { display:block; font-size:13px; font-weight:600; color:#1a2b45; }
.run-month   { display:block; font-size:11.5px; color:#7a90b0; margin-top:2px; }

/* -- Status badges ------------------------------------------------------- */
.status-badge        { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.status-badge--amber  { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
.status-badge--green  { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.status-badge--blue   { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.status-badge--purple { background:#f5f3ff; color:#7c3aed; border:1px solid #ddd6fe; }
.status-badge--red    { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
.status-badge--teal   { background:#f0fdfa; color:#0d9488; border:1px solid #99f6e4; }
.status-badge--indigo { background:#eef2ff; color:#4338ca; border:1px solid #c7d2fe; }
.status-badge--gray   { background:#f4f7fb; color:#64748b; border:1px solid #e4eaf2; }

/* -- Row hamburger menu -------------------------------------------------- */
.col-actions { width:56px; text-align:center; }
.row-menu    { position:relative; display:inline-block; }
.row-menu-btn {
  width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2;
  background:#fff; cursor:pointer; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:3.5px; padding:0;
  transition:background .15s, border-color .15s;
}
.row-menu-btn:hover { background:#f0f4fb; border-color:#c5d6f5; }
.hamburger-bar { display:block; width:14px; height:2px; background:#4a5e78; border-radius:2px; }
.row-menu-dropdown {
  position:fixed; z-index:9999;
  background:#fff; border:1px solid #e4eaf2; border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); min-width:160px; overflow:hidden;
}
.row-menu-item {
  display:flex; align-items:center; gap:9px; width:100%;
  padding:9px 14px; font-size:13px; font-weight:500; color:#1a2b45;
  background:none; border:none; cursor:pointer; text-align:left;
  font-family:inherit; transition:background .12s;
}
.row-menu-item:hover:not(:disabled) { background:#f4f7fb; }
.row-menu-item--danger { color:#dc2626; }
.row-menu-item--danger:hover:not(:disabled) { background:#fff5f5; }
.row-menu-item:disabled { opacity:.5; cursor:not-allowed; }
.row-menu-icon        { font-size:12px; width:14px; flex-shrink:0; }
.row-menu-icon--blue  { color:#004AAD; }
.row-menu-icon--green { color:#16a34a; }
.row-menu-divider     { height:1px; background:#f0f4f8; margin:3px 0; }

/* -- Pagination ---------------------------------------------------------- */
.pagination-bar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #f0f4f8; flex-wrap:wrap; gap:10px; }
.page-info      { font-size:13px; color:#7a90b0; }
.page-btns      { display:flex; gap:4px; }
.page-btn       { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; font-size:13px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.page-btn:hover:not(:disabled) { background:#f0f4fb; border-color:#c5d6f5; color:#004AAD; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-btn--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }
.page-btn--active:hover { background:#004AAD; color:#fff; }

/* -- Responsive ---------------------------------------------------------- */
@media (max-width:1024px) { .stat-grid { grid-template-columns:repeat(2,1fr); } }
@media (max-width:640px)  {
  .stat-grid       { grid-template-columns:1fr; }
  .page-header     { flex-direction:column; }
  .chart-y-axis    { width:36px; font-size:10px; }
  .filters-bar     { flex-direction:column; align-items:stretch; }
  .search-wrap     { max-width:100%; }
  .date-range-wrap { flex:1; }
  .filter-date     { flex:1; }
  .pagination-bar  { flex-direction:column; align-items:center; }
}

/* ── Create Payrun Modal ─────────────────────────────────────────────── */
.cr-backdrop { position:fixed; inset:0; background:rgba(10,20,50,.45); z-index:1040; }
.cr-overlay  { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; padding:16px; pointer-events:none; opacity:0; transition:opacity .2s; }
.cr-overlay.open { opacity:1; pointer-events:all; }
.cr-box      { background:#fff; border-radius:16px; width:100%; max-width:540px; box-shadow:0 24px 64px rgba(0,0,0,.2); overflow:hidden; transform:translateY(14px); transition:transform .22s ease; display:flex; flex-direction:column; max-height:92vh; }
.cr-overlay.open .cr-box { transform:translateY(0); }

/* header */
.cr-hd       { display:flex; align-items:flex-start; justify-content:space-between; padding:22px 24px 18px; border-bottom:1px solid #f0f4f8; flex-shrink:0; background:linear-gradient(135deg,#004AAD 0%,#1a6ed8 100%); }
.cr-hd-inner { display:flex; align-items:center; gap:14px; }
.cr-hd-icon  { width:42px; height:42px; border-radius:11px; background:rgba(255,255,255,.18); color:#fff; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
.cr-title    { font-size:16px; font-weight:700; color:#fff; margin:0 0 2px; }
.cr-sub      { font-size:12.5px; color:rgba(255,255,255,.75); margin:0; }
.cr-close    { background:rgba(255,255,255,.15); border:none; color:#fff; width:32px; height:32px; border-radius:8px; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:background .15s; }
.cr-close:hover { background:rgba(255,255,255,.28); }

/* body */
.cr-bd       { padding:24px; overflow-y:auto; flex:1; }

/* type picker */
.cr-type-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.cr-type-card  { display:flex; flex-direction:column; align-items:flex-start; gap:10px; background:#f8fafc; border:2px solid #e4eaf2; border-radius:13px; padding:20px 18px; cursor:pointer; text-align:left; transition:all .15s; font-family:inherit; position:relative; }
.cr-type-card:hover { border-color:#004AAD; background:#f0f5ff; }
.cr-type-icon  { width:44px; height:44px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.cr-type-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
.cr-type-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
.cr-type-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.cr-type-desc  { font-size:12.5px; color:#7a90b0; margin:0; line-height:1.6; }
.cr-type-arrow { position:absolute; bottom:16px; right:16px; color:#004AAD; font-size:13px; opacity:0; transition:opacity .15s; }
.cr-type-card:hover .cr-type-arrow { opacity:1; }

/* form */
.cr-form        { display:flex; flex-direction:column; gap:16px; }
.cr-field-row   { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.cr-field       { display:flex; flex-direction:column; gap:5px; }
.cr-label       { font-size:12.5px; font-weight:600; color:#4a5e78; }
.cr-req         { color:#e03535; }
.cr-input       { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; font-family:inherit; width:100%; box-sizing:border-box; }
.cr-input:focus { border-color:#004AAD; }

/* upload banner */
.cr-upload-banner      { display:flex; align-items:center; justify-content:space-between; gap:12px; background:#f0f5ff; border:1px solid #c5d6f5; border-radius:10px; padding:12px 16px; margin-bottom:16px; }
.cr-upload-banner-left { display:flex; align-items:flex-start; gap:10px; flex:1; }
.cr-banner-icon        { color:#004AAD; font-size:14px; flex-shrink:0; margin-top:1px; }
.cr-upload-banner-left p { font-size:12.5px; color:#1e3a6e; margin:0; line-height:1.6; }
.cr-template-btn          { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:12.5px; font-weight:600; padding:7px 13px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; transition:background .15s; flex-shrink:0; font-family:inherit; }
.cr-template-btn:hover:not(:disabled) { background:#003b8e; }
.cr-template-btn:disabled { opacity:.6; cursor:not-allowed; }

/* file upload */
.cr-file-wrap       { position:relative; border:2px dashed #c5d6f5; border-radius:10px; padding:20px; cursor:pointer; transition:border-color .15s; background:#f8fafc; }
.cr-file-wrap:hover,
.cr-file-wrap--has  { border-color:#004AAD; background:#f0f5ff; }
.cr-file-input      { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.cr-file-inner      { display:flex; flex-direction:column; align-items:center; gap:8px; pointer-events:none; }
.cr-file-icon       { font-size:26px; color:#94a3b8; }
.cr-file-icon--ok   { color:#16a34a; }
.cr-file-text       { font-size:13px; color:#4a5e78; text-align:center; }

/* footer */
.cr-ft          { display:flex; align-items:center; justify-content:space-between; padding:16px 24px; border-top:1px solid #f0f4f8; flex-shrink:0; background:#fafbfd; }
.cr-btn-ghost   { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#64748b; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; transition:background .15s; }
.cr-btn-ghost:hover { background:#e4eaf2; }
.cr-btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; transition:background .15s; }
.cr-btn-primary:hover:not(:disabled) { background:#003b8e; }
.cr-btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.me-1 { margin-right:4px; }

.btn-export-sm { display:inline-flex; align-items:center; gap:5px; background:#fff; color:#16a34a; border:1px solid #bbf7d0; font-size:12px; font-weight:600; padding:8px 12px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.btn-export-sm:hover:not(:disabled) { background:#f0fdf4; }
.btn-export-sm--pdf { color:#dc2626; border-color:#fecaca; }
.btn-export-sm--pdf:hover:not(:disabled) { background:#fff5f5; }
.btn-export-sm:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.col-picker-btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
.col-picker-btn-ghost:hover { background:#e4eaf2; }
.col-picker-btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; transition:background .15s; }
.col-picker-btn-primary:hover:not(:disabled) { background:#003b8e; }
.col-picker-btn-primary:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

/* -- Delete modal --------------------------------------------------------- */
.del-backdrop  { position:fixed; inset:0; background:rgba(10,20,40,.5); z-index:1060; display:flex; align-items:center; justify-content:center; padding:20px; }
.del-box       { background:#fff; border-radius:16px; width:100%; max-width:400px; padding:28px 28px 24px; text-align:center; box-shadow:0 24px 60px rgba(0,0,0,.2); }
.del-icon-wrap { width:56px; height:56px; border-radius:14px; background:#fff5f5; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
.del-icon      { font-size:22px; color:#dc2626; }
.del-title     { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 10px; }
.del-desc      { font-size:13px; color:#64748b; line-height:1.6; margin:0 0 22px; }
.del-actions   { display:flex; gap:10px; }
.del-btn-cancel  { flex:1; padding:9px 0; background:#f4f7fb; color:#64748b; border:1px solid #dde5f0; border-radius:9px; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .15s; }
.del-btn-cancel:hover:not(:disabled) { background:#e4eaf2; }
.del-btn-confirm { flex:1; padding:9px 0; background:#dc2626; color:#fff; border:none; border-radius:9px; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .15s; display:inline-flex; align-items:center; justify-content:center; gap:6px; }
.del-btn-confirm:hover:not(:disabled) { background:#b91c1c; }
.del-btn-cancel:disabled, .del-btn-confirm:disabled { opacity:.55; cursor:not-allowed; }
</style>